'use client';

import classnames from 'classnames';
import { useSearchParams } from 'next/navigation';
import { type FC, useEffect } from 'react';

import Button from 'molecules/Button';
import ContactSidebar from 'molecules/ContactSideBar';
import RichText from 'molecules/RichText';

import styles from 'components/Heros/SideNavHero/page-header.module.scss';

import { isNonEmptyArray, stringToId } from 'utils/functions';

import type { SideNaveHeroProps } from 'components/Heros/SideNavHero/config';

const SideNavHero: FC<SideNaveHeroProps> = ({ options, contactSideBar, backgroundColor }) => {
  const searchParams = useSearchParams();

  const smoothScroll = (id: string, scroll = true) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarOffset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - navbarOffset, behavior: scroll ? 'smooth' : 'auto' });
    }
  };

  useEffect(() => {
    const id = searchParams.get('id');

    if (id) {
      smoothScroll(id, false);
    }
  }, [searchParams]);

  return (
    <>
      <section
        className={classnames(styles.pageHeaderWrapper, {
          [styles.bkGray]: backgroundColor === 'grey',
          [styles.bkWhite]: backgroundColor === 'white',
        })}
      >
        <div className={styles.pageHeaderContainer}>
          <div className={styles.pageHeaderInner}>
            {isNonEmptyArray(options) &&
              options.map(({ eyebrow, body, ctas }, index) => (
                <div key={index} className={styles.content}>
                  {eyebrow && (
                    <h1 id={stringToId(eyebrow)} className={styles.pageHeaderTitle}>
                      {eyebrow}
                    </h1>
                  )}
                  {body && <RichText className={styles.pageHeaderBodyTextWrapper} content={body} />}
                  <div className={styles.ctaGroup}>
                    {isNonEmptyArray(ctas) &&
                      ctas.map(
                        (cta, index1) =>
                          cta?.label &&
                          cta?.link &&
                          (cta.type === 'arrow' ? (
                            <Button
                              key={index1}
                              className={styles.arrowLink}
                              direction="right"
                              url={cta.link}
                              iconId="arrow-right"
                            >
                              {cta.label}
                            </Button>
                          ) : (
                            <Button
                              className={styles.button}
                              key={index1}
                              url={cta.link}
                              color={cta.type === 'teal-button' ? 'teal' : 'gray'}
                              label={cta?.label}
                            />
                          )),
                      )}
                  </div>
                </div>
              ))}
          </div>
          <div className={styles.sideNav}>
            <ContactSidebar {...contactSideBar} className={styles.sideBar} />
          </div>
        </div>
      </section>
    </>
  );
};

export default SideNavHero;
